import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { registerSW } from 'virtual:pwa-register';
import { AppB } from './AppB';
import { configureLogger, log } from '../app/logger';
import { installGlobalHandlers } from '../app/globalHandlers';
import { bootstrapApp } from '../app/bootstrap';
import { notifyUpdateReady } from '../app/swUpdate';
import './styles.css';

/**
 * Touchline B — entry point for B's own origin (design spec §2.2/§7; engine baseline §4.1).
 *
 * This repeats A's boot sequence exactly, because the sequence is engine contract, not A design:
 * logger and global handlers first (an error thrown during startup is captured, OBS-1/NFR-8), then
 * `bootstrapApp()` fire-and-forget — it claims the writer lock, installs the ~49 seed templates
 * idempotently (so B's first run already has the library, scenario 4) and recovers incomplete
 * recordings, and it never rejects, so there is nothing to catch and nothing that may hold first
 * paint — then B's OWN service worker (`registerType: 'prompt'`, never autoUpdate: a forced reload
 * mid-match violates OBS-4), then render.
 *
 * B's IndexedDB is empty on first run by construction: `PRODUCT_DB_NAME` is origin-scoped by
 * IndexedDB itself, and B deploys to its own origin (requirements §2).
 */

configureLogger({
  console: import.meta.env.DEV,
  persist: true,
  now: () => Date.now(),
});

installGlobalHandlers();

void bootstrapApp();

log.info('boot', 'Touchline B starting', {
  buildId: __BUILD_ID__,
  mode: import.meta.env.MODE,
  // Installed-PWA detection is inverted on iOS (webkit.org/b/264218, safari-17-floor.md): with
  // manifest `display: standalone`, the media query that reports TRUE is `fullscreen`. Both are
  // recorded so the log answers "tab or installed?" on any platform.
  standalone:
    typeof window.matchMedia === 'function' &&
    (window.matchMedia('(display-mode: standalone)').matches ||
      window.matchMedia('(display-mode: fullscreen)').matches ||
      ('standalone' in window.navigator &&
        (window.navigator as { standalone?: boolean }).standalone === true)),
});

/**
 * OBS-4 — `prompt` obliges us to provide one (see A's main.tsx for the incident record: a waiting
 * worker nobody surfaces means a deployed fix never reaches the device). `notifyUpdateReady` holds
 * the apply function; B-8's notification strip renders the banner (`subscribeSwUpdate` /
 * `shouldPromptUpdate`). Until B-8 lands, an update waits silently — acceptable for the B-0
 * skeleton, whose only deploys are development ones; noted in the B-0 work state.
 */
const updateSW = registerSW({
  onNeedRefresh() {
    log.info('sw', 'a new bundle is waiting; offering it to the operator');
    notifyUpdateReady(updateSW);
  },
  onRegisterError(error) {
    log.exception('sw', 'service worker registration failed', error);
  },
});

const container = document.getElementById('root');
if (!container) {
  // Not a silent failure: without this the page would render blank with no explanation.
  throw new Error('Root container #root is missing from index-b.html.');
}

createRoot(container).render(
  <StrictMode>
    <AppB />
  </StrictMode>,
);
